import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/api';
import './propertyDetails.css';

const PropertyInvestmentsPage = () => {
    const { propertyId } = useParams();
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editingInvestment, setEditingInvestment] = useState(null);
    const [editForm, setEditForm] = useState({ cost: '', reason: '', investedBy: '' });
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const formatCurrency = (value) => '$' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0);
    const formatDate = (value) => {
        if (!value) return '-';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return '-';
        return parsed.toLocaleDateString();
    };

    const loadReport = async () => {
        if (!propertyId) return;
        setLoading(true);
        try {
            const res = await api.get(`/report/investments/${propertyId}`);
            if (res && res.data) {
                setReport(res.data);
            } else {
                setReport(null);
            }
        } catch (e) {
            console.error('failed to load investment report', e);
            setReport(null);
        } finally {
            setLoading(false);
        }
    };

    const openEdit = (investment) => {
        setEditingInvestment(investment);
        setEditForm({
            cost: investment.cost !== undefined ? String(investment.cost) : '',
            reason: investment.reason || '',
            investedBy: investment.investedBy || ''
        });
        setMessage({ type: '', text: '' });
    };

    const closeEdit = () => {
        setEditingInvestment(null);
        setEditForm({ cost: '', reason: '', investedBy: '' });
    };

    const onEditChange = (e) => setEditForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onEditSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put(`/investment/${editingInvestment.id}`, {
                cost: parseFloat(editForm.cost) || 0,
                reason: editForm.reason.trim(),
                investedBy: editForm.investedBy.trim()
            });
            closeEdit();
            await loadReport();
            setMessage({ type: 'success', text: 'Investment updated successfully.' });
        } catch (err) {
            console.error('failed to update investment', err);
            setMessage({ type: 'error', text: 'Failed to update investment; please try again.' });
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async (investment) => {
        if (!window.confirm('Delete this investment? This cannot be undone.')) return;
        try {
            await api.delete(`/investment/${investment.id}`);
            await loadReport();
            setMessage({ type: 'success', text: 'Investment deleted.' });
        } catch (err) {
            console.error('failed to delete investment', err);
            setMessage({ type: 'error', text: 'Failed to delete investment; please try again.' });
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, []);

    useEffect(() => {
        loadReport();
    }, [propertyId]);

    return (
        <div className="property-pnl-page property-investment-page">
            <div className="property-pnl-toolbar">
                <button className="btn-secondary" onClick={() => navigate(`/PropertyDetails/${propertyId}`)}>
                    ← Back to Property Details
                </button>
                <div className="property-pnl-title-block">
                    <h2>{report?.property?.name || 'Property'} Investment Report</h2>
                    <p>
                        {report?.property
                            ? `${report.property.address1}, ${report.property.city}, ${report.property.state}`
                            : 'Loading property...'}
                    </p>
                </div>
                <button type="button" className="btn-primary" onClick={loadReport}>Reload</button>
            </div>

            {message.text && (
                <div className={`app-notice ${message.type === 'success' ? 'app-notice-success' : 'app-notice-error'}`}>
                    {message.text}
                </div>
            )}

            {loading ? (
                <div className="property-pnl-card">
                    <p className="no-features">Loading investment report...</p>
                </div>
            ) : report ? (
                <>
                    <div className="investment-summary-grid">
                        <div className="property-pnl-card investment-summary-card">
                            <span className="investment-summary-label">Total Invested</span>
                            <strong>{formatCurrency(report.totalInvestment)}</strong>
                        </div>
                        <div className="property-pnl-card investment-summary-card">
                            <span className="investment-summary-label">Net Profit / Loss</span>
                            <strong className={report.totalRecordedNet >= 0 ? 'net-positive' : 'net-negative'}>
                                {formatCurrency(report.totalRecordedNet)}
                            </strong>
                        </div>
                        <div className="property-pnl-card investment-summary-card">
                            <span className="investment-summary-label">Investment Entries</span>
                            <strong>{report.count}</strong>
                        </div>
                        <div className="property-pnl-card investment-summary-card">
                            <span className="investment-summary-label">Average Investment</span>
                            <strong>{formatCurrency(report.averageInvestment)}</strong>
                        </div>
                    </div>

                    <div className="property-pnl-card investment-report-context">
                        <p>
                            Net profit/loss reflects all recorded monthly property activity across {report.recordedMonths || 0} month{report.recordedMonths === 1 ? '' : 's'} in the system.
                        </p>
                    </div>

                    <div className="property-pnl-card">
                        {report.investments && report.investments.length > 0 ? (
                            <div className="property-pnl-table-wrap">
                                <table className="pnl-table property-investment-table">
                                    <thead>
                                        <tr>
                                            <th>Date Added</th>
                                            <th>Invested By</th>
                                            <th>Reason</th>
                                            <th>Cost</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.investments.map((investment) => (
                                            <tr key={investment.id}>
                                                <td>{formatDate(investment.createdAt)}</td>
                                                <td>{investment.investedBy || '—'}</td>
                                                <td>{investment.reason}</td>
                                                <td>{formatCurrency(investment.cost)}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <button
                                                        type="button"
                                                        className="btn-secondary btn-inline"
                                                        onClick={() => openEdit(investment)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-secondary btn-inline btn-danger-inline"
                                                        style={{ marginLeft: 6 }}
                                                        onClick={() => onDelete(investment)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="no-features">No investments have been recorded for this property yet.</p>
                        )}
                    </div>
                </>
            ) : (
                <div className="property-pnl-card">
                    <p className="no-features">Unable to load the investment report for this property.</p>
                </div>
            )}

            {editingInvestment && (
                <div className="modal-overlay" onClick={closeEdit}>
                    <div className="modal-content property-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Edit Investment</h3>
                            <button className="modal-close" aria-label="Close edit investment dialog" onClick={closeEdit}>&times;</button>
                        </div>
                        <form onSubmit={onEditSubmit} className="modal-form">
                            <label htmlFor="inv-cost">Cost</label>
                            <input
                                id="inv-cost"
                                name="cost"
                                type="number"
                                step="0.01"
                                min="0"
                                value={editForm.cost}
                                onChange={onEditChange}
                                required
                            />
                            <label htmlFor="inv-reason">Reason</label>
                            <input
                                id="inv-reason"
                                name="reason"
                                type="text"
                                value={editForm.reason}
                                onChange={onEditChange}
                                required
                            />
                            <label htmlFor="inv-investedBy">Invested By</label>
                            <input
                                id="inv-investedBy"
                                name="investedBy"
                                type="text"
                                value={editForm.investedBy}
                                onChange={onEditChange}
                            />
                            <div className="modal-buttons modal-buttons-right">
                                <button type="submit" className="btn-primary" disabled={saving}>
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button type="button" className="btn-secondary" onClick={closeEdit}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropertyInvestmentsPage;